/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

import get from 'lodash/fp/get.js';
import keyBy from 'lodash/fp/keyBy.js';
import { mapObjIndexed } from 'ramda';
import {
  MODEL_TYPE_1_ID,
  MODEL_TYPE_2_ID,
  MODEL_TYPE_3_ID,
} from '@hbrisk/sp3-risk-model-support/models/modelTypes/ids.js';
import makeFormValidator from '@hbrisk/sp3-risk-model-support/utility/form/validation/makeFormValidator/index.js';
import findFieldDefinition from '@hbrisk/sp3-risk-model-support/utility/form/findFieldDefinition/index.js';
import getAutofiller from '@hbrisk/sp3-risk-model-support/utility/form/getAutofiller/index.js';
import { sections as type1Sections } from '@hbrisk/sp3-risk-model-support/models/modelPage/type1/sections.js';
import { sectionGroups as type1SectionGroups } from '@hbrisk/sp3-risk-model-support/models/modelPage/type1/sectionGroups.js';
import { sections as type2Sections } from '@hbrisk/sp3-risk-model-support/models/modelPage/type2/sections.js';
import { sectionGroups as type2SectionGroups } from '@hbrisk/sp3-risk-model-support/models/modelPage/type2/sectionGroups.js';
import { sections as type3Sections } from '@hbrisk/sp3-risk-model-support/models/modelPage/type3/sections.js';
import { sectionGroups as type3SectionGroups } from '@hbrisk/sp3-risk-model-support/models/modelPage/type3/sectionGroups.js';
import {
  DOWNLOADS_SECTION,
  REPAIR_COST_BY_INTENSITY_SECTION,
} from '@hbrisk/sp3-risk-model-support/models/modelPage/sectionIds.js';
import type1Fields from '@hbrisk/sp3-risk-model-support/models/form/type1/fields/index.js';
import type2Fields from '@hbrisk/sp3-risk-model-support/models/form/type2/fields/index.js';
import type3Fields from '@hbrisk/sp3-risk-model-support/models/form/type3/fields/index.js';
import { TYPE_1_MODEL_FORM } from '@hbrisk/sp3-risk-model-support/models/form/type1/name.js';
import { TYPE_2_MODEL_FORM } from '@hbrisk/sp3-risk-model-support/models/form/type2/name.js';
import { TYPE_3_MODEL_FORM } from '@hbrisk/sp3-risk-model-support/models/form/type3/name.js';
import {
  selectActiveSectionGroups,
  selectActiveSectionsInActiveGroups,
  selectActiveSectionsByGroup,
  selectErrorCountBySection,
  selectInitialized,
  selectWarningCountBySection,
} from '#selectors/ui/modelPage.js';
import * as formSelectors from '#selectors/ui/form/model.js';
import {
  makeAutofilledValueSelector,
  makePickedAutofilledValuesSelector,
  selectModelFormInputsBySliceWithAutofilledValues,
} from '#selectors/entities/models.js';
import {
  selectPendingProposalCountBySection,
  selectPendingProposalFieldNames,
} from '#selectors/entities/modelAiReview.js';
import urlFromSection from '#support/models/modelPage/urlFromSection.js';

const modelType1Base = {
  allSections: type1Sections,
  allSectionGroups: type1SectionGroups,
  description:
    'Full FEMA P-58 seismic assessment of a building from standard inputs - SUL/SEL, component-level loss, and collapse risk. For PML, due diligence, and existing-building screening.',
  fields: type1Fields,
  id: MODEL_TYPE_1_ID,
  modelFormName: TYPE_1_MODEL_FORM,
  name: 'Standard Model',
  shortName: 'Std',
  runButtonRunningText: 'Running ...',
  runButtonText: 'Run Model',
  submitRedirectSectionId: REPAIR_COST_BY_INTENSITY_SECTION,
  shouldShowRetrofitInfo: true,
};

const modelType2Base = {
  allSections: type2Sections,
  allSectionGroups: type2SectionGroups,
  description:
    'Everything in the Standard, plus user-defined structural properties, custom component inventories, and custom fragilities. For resilient new design, retrofit comparisons, Custom Structural Responses, and detailed assessments.',
  fields: type2Fields,
  id: MODEL_TYPE_2_ID,
  modelFormName: TYPE_2_MODEL_FORM,
  name: 'Advanced Model',
  shortName: 'Adv',
  runButtonRunningText: 'Running ...',
  runButtonText: 'Update and Run Model',
  submitRedirectSectionId: null,
  shouldShowRetrofitInfo: false,
};

const makeFieldNameValidator = fields => fullyQualifiedFieldName => {
  const definition = findFieldDefinition(fullyQualifiedFieldName, fields);
  if (definition === undefined) {
    throw new Error(`Field ${fullyQualifiedFieldName} is not defined in modelTypes constant`);
  }
  return undefined;
};

const getIsFieldInSection = fieldsByName => (field, section) =>
  get(`${field}.section`, fieldsByName) === section;

/*
Resolves a model type's configured post-submit redirect section into the url the router
registers for it. Null means the type does not redirect on submit. A section id that isn't one
of the type's own sections throws here, at module load, rather than silently producing a url the
router would bounce off its catch-all route.
 */
const calculateSubmitRedirectUrl = (sectionId, allSectionsById) => {
  if (sectionId == null) {
    return undefined;
  }
  const section = allSectionsById[sectionId];
  if (section === undefined) {
    throw new Error(`Section ${sectionId} is not defined in modelTypes constant`);
  }
  return urlFromSection(section);
};

const addDerived = type => {
  const { allSectionGroups, allSections, fields, id, modelFormName, submitRedirectSectionId } =
    type;
  const fieldsByName = keyBy('name', fields);
  const allSectionsById = keyBy('id', allSections);
  const formSelectorFunctions = mapObjIndexed(selector => selector(modelFormName), formSelectors);

  const isAutofillable = fullyQualifiedFieldName =>
    getAutofiller(fullyQualifiedFieldName) !== undefined;

  return {
    ...type,
    allSectionsById,
    fieldsByName,
    isAutofillable,
    isFieldInSection: getIsFieldInSection(fieldsByName),
    selectModelFormInputsBySliceWithAutofilledValues:
      selectModelFormInputsBySliceWithAutofilledValues(fields),
    makeAutofilledValueSelector: makeAutofilledValueSelector(fields),
    makePickedAutofilledValuesSelector: makePickedAutofilledValuesSelector(fields),
    selectActiveSectionGroups: selectActiveSectionGroups(id, allSectionGroups),
    selectActiveSectionsInActiveGroups: selectActiveSectionsInActiveGroups(id, allSections),
    selectActiveSectionsByGroup: selectActiveSectionsByGroup(
      fieldsByName,
      modelFormName,
      id,
      allSections
    ),
    selectErrorCountBySection: selectErrorCountBySection(fieldsByName, modelFormName),
    selectWarningCountBySection: selectWarningCountBySection(fieldsByName, modelFormName),
    selectPendingProposalCountBySection: selectPendingProposalCountBySection(
      fieldsByName,
      modelFormName
    ),
    selectPendingProposalFieldNames: selectPendingProposalFieldNames(fieldsByName, modelFormName),
    selectModelPageInitialized: selectInitialized(id),
    submitRedirectUrl: calculateSubmitRedirectUrl(submitRedirectSectionId, allSectionsById),
    validate: makeFormValidator(fields),
    validateFieldName: makeFieldNameValidator(fields),
    ...formSelectorFunctions,
  };
};

const modelType3Base = {
  allSections: type3Sections,
  allSectionGroups: type3SectionGroups,
  description:
    'A seismic hazard report for any U.S. address, compiled from authoritative public datasets — covering shaking, liquefaction, landslide, tsunami, fault zones, shake map history, and soil/Vs30.',
  fields: type3Fields,
  id: MODEL_TYPE_3_ID,
  modelFormName: TYPE_3_MODEL_FORM,
  name: 'Site Hazards Model',
  shortName: 'SH',
  runButtonRunningText: 'Running ...',
  runButtonText: 'Run Site Hazards',
  submitRedirectSectionId: DOWNLOADS_SECTION,
  shouldShowRetrofitInfo: false,
};

const type1 = addDerived(modelType1Base);
const type2 = addDerived(modelType2Base);
const type3 = addDerived(modelType3Base);

const modelTypes = [type1, type2, type3];

const modelTypesById = keyBy('id', modelTypes);
const modelTypesByForm = keyBy('modelFormName', modelTypes);

export {
  MODEL_TYPE_1_ID,
  MODEL_TYPE_2_ID,
  MODEL_TYPE_3_ID,
  modelTypes,
  modelTypesById,
  modelTypesByForm,
};
